import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import ReactFlow, {
  addEdge,
  Background,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import { Panel } from 'reactflow';
import { FiZoomIn, FiZoomOut, FiFolder, FiChevronDown } from 'react-icons/fi';
import { TbArrowsMaximize } from 'react-icons/tb';
import { FiX } from 'react-icons/fi';
import { AiOutlineEye } from 'react-icons/ai';
import 'reactflow/dist/style.css';
import { Globe } from 'lucide-react';
import { FaToolbox } from 'react-icons/fa';

import {
  WORKFLOW_API_URL,
  getSpecificWorkflow,
  executeWorkflow,
} from '../../services/workflowApi';
import TriggerNode from '../workflow3/TriggerNode';
import ActionNode from '../workflow3/ActionNode';
import ConditionalNode from '../workflow3/ConditionalNode';
import DataNodeModal from '../workflow3/DataNodeModal'; // Import the DataNodeModal
import DataNode from '../workflow3/DataNode'; // Import the DataNode component
import AgentNode from '../workflow3/AgentNode'; // Import the AgentNode component
import AgentNodeModal from '../workflow3/AgentNodeModal'; // Import the AgentNodeModal
import { DataState } from '../../context/DataProvider';
import TitleDescriptionModal from '../workflow3/TitleDescriptionModal'; // Importing the new modal
import { integrationData } from '../../utils/constants';
import IntegrationsModal from './IntegrationsModal';
import ExecutionHistorySidebar from './ExecutionHistorySidebar';
// Web Socket Provider
import { useWebSocket } from '../../context/WebSocketProvider';
import NodeExecutionComponent from './NodeExecutionComponent';
import DataArrayNode from '../workflow3/DataArrayNode';
import DataSplitterNode from '../workflow3/DataSplitterNode';
import FilesModal from './FilesModal';
import { useWorkflowExecutions } from '../../context/WorkflowExecutionsProvider';
import ToolInputNode from '../workflow3/ToolInputNode';
import ToolOutputNode from '../workflow3/ToolOutputNode';
import RestructureNode from '../workflow3/RestructureNode';
import CustomAgentNode from '../workflow3/CustomAgentNode';
import OperationsNode from '../workflow3/OperationsNode';
import GlobalStateModal from './GlobalStateModal';
import UnwrapArrayNode from '../workflow3/UnwrapArrayNode';
import LoopBreakNode from '../workflow3/LoopBreakNode';
import StructuralConditionalNode from '../workflow3/StructuralConditionalNode';

const initialNodes = [];
const initialEdges = [];

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  conditional: ConditionalNode, // Add this
  data: DataNode, // Add DataNode here
  dataSplitter: DataSplitterNode, // New custom node type
  dataArray: DataArrayNode,
  agent: AgentNode, // Add AgentNode here
  // --- NEW NODE TYPES ---
  toolInput: ToolInputNode,
  restructure: RestructureNode,
  toolOutput: ToolOutputNode,
  customAgent: CustomAgentNode,
  operations: OperationsNode,
  unwrapArray: UnwrapArrayNode,
  loopBreak: LoopBreakNode,
  structuralConditional: StructuralConditionalNode,
};

const WorkflowExecutionWindow = ({
  closeModal,
  mode = 'create',
  workflowId,
  currentExecutionId = '',
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { userGoogleId } = DataState();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit' or 'integrate'
  const [nodeType, setNodeType] = useState(''); // 'trigger' or 'action'
  // const [selectedNode, setSelectedNode] = useState(null);
  // Use:
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [workflowTitle, setWorkflowTitle] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [nodeIdCounter, setNodeIdCounter] = useState(1); // Counter for node IDs
  // const [integrationsModalOpen, setIntegrationsModalOpen] = useState(false);
  const [selectedIntegrationAppOptions, setSelectedIntegrationAppOptions] =
    useState([]);
  // const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [selectedDataSourceFiles, setSelectedDataSourceFiles] = useState([]);

  const [agentsData, setAgentsData] = useState([]); // State for agents list

  const [executionId, setExecutionId] = useState(currentExecutionId);
  // State to toggle execution history sidebar visibility.
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const { subscribe, isConnected } = useWebSocket();
  const { workflowExecutions } = useWorkflowExecutions(); // 2. Consume the context

  // 3. Find the current workflow's overall status from the context
  const workflowStatus = useMemo(() => {
    return workflowExecutions.find((exec) => exec.execution_id === executionId)
      ?.status;
  }, [workflowExecutions, executionId]);

  // --- NEW: State for live global_state data ---
  const [globalStateData, setGlobalStateData] = useState(null);
  const [isGlobalStateModalOpen, setIsGlobalStateModalOpen] = useState(false);

  useEffect(() => {
    // Only subscribe if an executionId exists
    if (!executionId) return;

    const eventKey = `node_status_notification_${executionId}`;

    // --- Event key for chat updates ---
    const chatEventKey = `workflow_execution_${executionId}_node_chat`;

    // --- NEW: Event key for global state updates ---
    const globalStateEventKey = `workflow_global_state_update_${executionId}`;

    // Subscribe to the event via the context provider.
    const unsubscribe = subscribe({
      [eventKey]: (data) => {
        if (data.status === 'failed') {
          toast.error('Workflow node execution failed');
        } else {
          toast.success(`Workflow Node Status Updated: ${data.status}`);
        }
        const baseId = (data?.node_id || '').split(/[#@]/)[0];
        if (baseId === data?.node_id) {
          setNodes((prev_nodes) =>
            prev_nodes.map((prev_node) =>
              data.node_id === prev_node.id
                ? {
                    ...prev_node,
                    data: {
                      ...prev_node.data,
                      status: data.status,
                      output: data.output,
                      error: data.error,
                      approvalNeededTools: data.approvalNeededTools || {},
                      improvement_notes_for_developer: [
                        ...(prev_node?.data?.improvement_notes_for_developer ??
                          []),
                        ...(data?.improvement_note_for_developer
                          ? [data.improvement_note_for_developer]
                          : []),
                      ],
                    },
                  }
                : prev_node,
            ),
          );
        } else {
          // Prepare the new split data.
          const newSplit = {
            id: data.node_id, // Original full node id
            status: data.status,
            output: data.output,
            error: data.error,
            approvalNeededTools: data.approvalNeededTools || {},
          };

          setNodes((prev_nodes) =>
            prev_nodes.map((prev_node) => {
              // Find the node with the matching baseId.
              if (baseId === prev_node.id) {
                // Retrieve the existing splits array (if any).
                const existingSplits = prev_node.data?.splits || [];
                // Check if a split with the same id already exists.
                const splitIndex = existingSplits.findIndex(
                  (split) => split.id === newSplit.id,
                );
                let updatedSplits;
                if (splitIndex !== -1) {
                  // Update the existing split.
                  updatedSplits = existingSplits.map((split) =>
                    split.id === newSplit.id
                      ? { ...split, ...newSplit }
                      : split,
                  );
                } else {
                  // Append the new split.
                  updatedSplits = [...existingSplits, newSplit];
                }
                // Determine parent status based on split statuses
                const allStatuses = updatedSplits.map((s) => s.status);
                let parentStatus = prev_node.data.status;

                if (allStatuses.includes('approvalNeeded')) {
                  parentStatus = 'approvalNeeded';
                } else if (allStatuses.includes('failed')) {
                  parentStatus = 'failed';
                } else if (allStatuses.includes('running')) {
                  parentStatus = 'running';
                } else if (allStatuses.includes('queued')) {
                  parentStatus = 'queued';
                } else if (allStatuses.includes('pending')) {
                  parentStatus = 'pending';
                } else if (allStatuses.includes('success')) {
                  parentStatus = 'success';
                } else if (allStatuses.includes('skipped')) {
                  parentStatus = 'skipped';
                }
                // } else if (allStatuses.every((s) => s === "success")) {
                //   parentStatus = "success";
                // } else if (allStatuses.every((s) => s === "pending")) {
                //   parentStatus = "pending";
                // } else if (allStatuses.every((s) => s === "skipped")) {
                //   parentStatus = "skipped";
                // }

                return {
                  ...prev_node,
                  data: {
                    ...prev_node.data,
                    status: parentStatus,
                    splits: updatedSplits,
                  },
                };
              }
              return prev_node;
            }),
          );
        }
      },
      // --- NEW: Handler for Node Chat Notifications ---
      [chatEventKey]: (data) => {
        const { node_chat, node_id } = data;

        if (!node_chat || !node_id) return;

        const baseId = node_id.split(/[#@]/)[0];

        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            // --- Case 1: Chat for a main node ---
            if (node.id === node_id) {
              const currentChats = node.data.chats || [];
              return {
                ...node,
                data: {
                  ...node.data,
                  chats: [...currentChats, node_chat],
                },
              };
            }

            // --- Case 2: Chat for a split node ---
            if (node.id === baseId) {
              const updatedSplits = (node.data.splits || []).map((split) => {
                if (split.id === node_id) {
                  const currentChats = split.chats || [];
                  return {
                    ...split,
                    chats: [...currentChats, node_chat],
                  };
                }
                return split;
              });
              return {
                ...node,
                data: {
                  ...node.data,
                  splits: updatedSplits,
                },
              };
            }

            // If not the target node or parent, return as is
            return node;
          }),
        );
      },
      // --- NEW: Handler for global state updates ---
      [globalStateEventKey]: (data) => {
        toast.success('Global State has been updated.');
        setGlobalStateData(data.global_state); // Update the state with the new data
      },
    });

    // Cleanup: Unsubscribe when the component unmounts or executionId changes.
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [executionId, subscribe, isConnected]);

  const fetchJiraProjects = async (appsEventsData) => {
    try {
      const response = await fetch(
        `${WORKFLOW_API_URL}/search/jiraProjects?start=0&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: 'include',
        },
      );
      if (response.ok) {
        const data = await response.json();
        appsEventsData.map((triggerType) => {
          if (triggerType.type !== 'webhook') return triggerType; // Skip non-webhook triggers

          return {
            ...triggerType,
            options: triggerType.options.map((option) => {
              if (option.value === 'jira') {
                // Find the "projects" parameter and set its value to an empty list
                const updatedParameters = option.parameters.map((param) => {
                  if (param.param === 'projects') {
                    return {
                      ...param,
                      enum: ['All', ...data.projects.map((proj) => proj.key)],
                    }; // Set projects to an empty list
                  }
                  return param; // Return other parameters unchanged
                });

                return { ...option, parameters: updatedParameters };
              }
              return option; // Return other options unchanged
            }),
          };
        });
      } else {
        await response.json();
      }
    } catch (error) {
      toast.error('Error fetching Jira projects');
    }
  };

  const fetchAppsandEventsData = async () => {
    try {
      const response = await fetch(
        `${WORKFLOW_API_URL}/canvas/appsAndTriggers`,
        {
          method: 'POST', // Use the appropriate HTTP method (POST, PUT, etc.)
          headers: {
            'Content-Type': 'application/json', // Specify that you're sending JSON
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: 'include',
          body: JSON.stringify({ apps: selectedIntegrationAppOptions }), // Convert the payload to a JSON string
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const data = await response.json();
      setAgentsData(data.agents || []); // <<< Store agents data
      fetchJiraProjects(data.appsEventsData || []);
    } catch (error) {
      toast.error('Error fetching integration data');
    }
  };
  useEffect(() => {
    // Fetch the app data from the API endpoint

    const setStartingCanvas = async () => {
      try {
        let data = null;

        data = await getSpecificWorkflow(workflowId, userGoogleId, executionId);
        // setNodes(data.nodes || []);
        // setEdges(data.edges || []);
        // setWorkflowTitle(data.title || "");
        // setWorkflowDescription(data.description || "");

        if (data) {
          setEdges(
            (data.edges || []).map((edge) => {
              return {
                ...edge,
                animated: true,
                markerEnd: { type: 'arrow' },
                type: 'smoothstep',
              };
            }),
          );
          const nodesWithDataSourcesAdded = (data.nodes || []).map((node) => {
            if (node.type !== 'data') {
              return node;
            }
            const data_source_id = node.data.selectedSource || '';
            const selected_data_source = (data.data_sources || []).find(
              (source) => data_source_id.file_id === source.file_id,
            );
            return {
              ...node,
              data: { ...node.data, selectedSource: selected_data_source },
            };
          });

          // --- NEW: Set the initial global_state from the execution doc ---
          setGlobalStateData(data.global_state || null);

          setNodes(nodesWithDataSourcesAdded || []);
          setWorkflowTitle(data.title || '');
          setWorkflowDescription(data.description || '');
          setSelectedIntegrationAppOptions(data.apps_expanded || []);
          setSelectedDataSourceFiles(data.data_sources || []);

          // Find the maximum ID in the fetched nodes
          const maxNodeId = (data.nodes || []).reduce((max, node) => {
            const nodeId = parseInt(node.id, 10); // Assuming node.id is a number or can be parsed to a number
            return nodeId > max ? nodeId : max;
          }, 0);

          // Set the next NodeId state to be maxNodeId + 1
          setNodeIdCounter(maxNodeId + 1);
        }
      } catch (error) {
        toast.error('Error fetching workflow data');
      }
    };

    // fetchAppsData();
    // fetchAppsTriggersData();
    setStartingCanvas();

    // eslint-disable-next-line
  }, [executionId]); // Empty dependency array to call this effect only once on component mount

  useEffect(() => {
    fetchAppsandEventsData();

    // eslint-disable-next-line
  }, [selectedIntegrationAppOptions]);

  const handleNodeClick = (event, node) => {
    // setSelectedNode(node); // Deep copy of node data
    setSelectedNodeId(node.id);
    setNodeType(node.type);
    setModalType('edit');
    setModalOpen(true);
  };
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId],
  );

  const handleIntegrationsButtonClick = () => {
    setNodeType(null);
    setModalType('integrate');
    setModalOpen(true);
  };

  const handleSaveNode = (nodeData) => {
    if (modalType === 'add') {
      const newNode = {
        id: `${nodeIdCounter}`, // Generate a unique ID
        position: { x: Math.random() * 400, y: Math.random() * 400 },
        data: { ...nodeData.data }, // Ensure unique data for each node
        type: nodeType,
      };
      setNodes((nds) => [...nds, newNode]);
      setNodeIdCounter((prev) => prev + 1); // Increment the counter
    } else if (modalType === 'edit') {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeData.id
            ? { ...node, data: { ...nodeData.data } }
            : node,
        ),
      );
    }
    setModalOpen(false);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) =>
      eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    );
    setModalOpen(false);
  };

  const handleConnect = (params) => {
    if (params.source === params.target) {
      alert('Cannot connect a node to itself.'); // Show a warning or handle it in your UI
      return; // Do not add the edge
    }
    // Create the edge ID using source and target
    // const edgeId = `e${params.source}-${params.target}`;
    // Create a unique edge ID by including the handle IDs (e.g., source and target positions) to differentiate
    const edgeId = `e-${params.source}-${params.sourceHandle || 'default'}-${params.target}-${params.targetHandle || 'default'}`;

    setEdges((eds) =>
      addEdge(
        {
          ...params,
          id: edgeId,
          animated: true,
          markerEnd: { type: 'arrow' },
          type: 'smoothstep',
        },
        eds,
      ),
    );
  };

  const handleEdgeClick = (event, edge) => {
    event.stopPropagation(); // Prevent triggering other click events
    // setEdges((eds) => eds.filter((e) => e.id !== edge.id)); // Remove the clicked edge
  };

  const handleCloseCanvas = () => {
    closeModal();
  };
  const handleSaveTitleDescription = (title, description) => {
    setWorkflowTitle(title);
    setWorkflowDescription(description);
    setTitleModalOpen(false);
  };

  const handleExecuteWorkflow = async () => {
    try {
      const data = await executeWorkflow(workflowId, userGoogleId);

      toast.success(data.message || 'Success');
      setExecutionId(data.execution_id || '');
    } catch (error) {
      toast.error(error);
    }
    // toast.success("Workflow Executed")
  };

  // saved comment
  return (
    <ReactFlowProvider>
      {/* Dark theme ui using zinc colors */}
      <div className="fixed inset-0 z-999 flex  flex-col bg-zinc-900">
        <WorkflowHeader
          workflowTitle={workflowTitle}
          onTitleEdit={() => setTitleModalOpen(true)}
          onAddAppIntegration={handleIntegrationsButtonClick}
          // onUndo={handleUndo}
          // onRedo={handleRedo}
          onClose={handleCloseCanvas}
          handleExecuteWorkflow={handleExecuteWorkflow}
          onShowHistory={() => setIsHistoryOpen(true)} // Toggle history sidebar
          onOpenFiles={() => setFilesModalOpen(true)}
          onShowGlobalState={() => setIsGlobalStateModalOpen(true)} // Pass new handler
        />

        <div className="flex-grow">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick} // Add the edge click handler here
            fitView
            nodeTypes={nodeTypes}
          >
            <Background />
            <CustomControls />
            <MiniMap
              position="bottom-left"
              style={{
                backgroundColor: '#18181b', // zinc-900
                border: '1px solid #3f3f46', // zinc-700
                borderRadius: '6px',
              }}
              nodeColor="#52525b" // zinc-600
              maskColor="rgba(24, 24, 27, 0.6)" // zinc-900 with opacity
            />
          </ReactFlow>
        </div>

        {modalOpen && nodeType === 'action' && (
          <NodeExecutionComponent
            node={selectedNode}
            onClose={() => setModalOpen(false)}
            workflowId={workflowId}
            executionId={executionId}
            workflowStatus={workflowStatus}
          />
        )}
        {modalOpen &&
          [
            'toolInput',
            'toolOutput',
            'restructure',
            'customAgent',
            'operations',
            'unwrapArray',
            'structuralConditional',
          ].includes(nodeType) && (
            <NodeExecutionComponent
              node={selectedNode}
              onClose={() => setModalOpen(false)}
              workflowId={workflowId}
              executionId={executionId}
              workflowStatus={workflowStatus}
            />
          )}

        {modalOpen && nodeType === 'trigger' && (
          // <TriggerNodeModal
          //   mode={modalType}
          //   nodeData={modalType === "edit" ? selectedNode : null}
          //   onSave={handleSaveNode}
          //   onDelete={handleDeleteNode}
          //   onClose={() => setModalOpen(false)}
          //   appEventData={appEventData}
          //   viewOnly={true}
          // />
          <NodeExecutionComponent
            node={selectedNode}
            onClose={() => setModalOpen(false)}
            workflowId={workflowId}
            executionId={executionId}
          />
        )}
        {/* // Add the Conditional Node Modal */}
        {modalOpen && nodeType === 'conditional' && (
          <NodeExecutionComponent
            node={selectedNode}
            onClose={() => setModalOpen(false)}
            workflowId={workflowId}
            executionId={executionId}
          />
        )}
        {/* Modals for all node types */}
        {modalOpen && nodeType === 'data' && (
          <DataNodeModal
            mode={modalType}
            nodeData={modalType === 'edit' ? selectedNode : null}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
            onClose={() => setModalOpen(false)}
            dataSources={selectedDataSourceFiles}
            viewOnly={true}
          />
        )}
        {modalOpen && nodeType === 'agent' && (
          <AgentNodeModal
            mode={modalType}
            nodeData={modalType === 'edit' ? selectedNode : null}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
            onClose={() => setModalOpen(false)}
            agentsList={agentsData}
            viewOnly={true}
          />
        )}

        {modalOpen && nodeType === 'dataSplitter' && (
          <NodeExecutionComponent
            node={selectedNode}
            onClose={() => setModalOpen(false)}
            workflowId={workflowId}
            executionId={executionId}
          />
        )}
        {modalOpen && nodeType === 'dataArray' && (
          <NodeExecutionComponent
            node={selectedNode}
            onClose={() => setModalOpen(false)}
            workflowId={workflowId}
            executionId={executionId}
          />
        )}
        {modalOpen && modalType === 'integrate' && (
          <IntegrationsModal
            integrationData={integrationData}
            selectedDataSourceFiles={selectedDataSourceFiles}
            setSelectedDataSourceFiles={setSelectedDataSourceFiles}
            selectedIntegrationAppOptions={selectedIntegrationAppOptions}
            setSelectedIntegrationAppOptions={setSelectedIntegrationAppOptions}
            onClose={() => setModalOpen(false)}
            viewOnly={true}
          />
        )}
        {titleModalOpen && (
          <TitleDescriptionModal
            initialTitle={workflowTitle}
            initialDescription={workflowDescription}
            onClose={() => setTitleModalOpen(false)}
            onSave={handleSaveTitleDescription}
            viewOnly={true}
          />
        )}
        {/* Render the Execution History Sidebar if toggled open */}
        {isHistoryOpen && (
          <ExecutionHistorySidebar
            workflowId={workflowId}
            executionId={executionId}
            setExecutionId={setExecutionId}
            onClose={() => setIsHistoryOpen(false)}
          />
        )}

        {/* Files Modal */}
        {filesModalOpen && (
          <FilesModal
            taskId={executionId}
            onClose={() => {
              setFilesModalOpen(false);
            }}
          />
        )}
        {/* --- NEW: Modal for displaying Global State --- */}
        {isGlobalStateModalOpen && (
          <GlobalStateModal
            globalState={globalStateData}
            onClose={() => setIsGlobalStateModalOpen(false)}
          />
        )}
      </div>
    </ReactFlowProvider>
  );
};

export default WorkflowExecutionWindow;

const CustomControls = () => {
  const { zoomIn: flowZoomIn, zoomOut: flowZoomOut, fitView } = useReactFlow();

  const handleZoomIn = useCallback(() => {
    flowZoomIn({ duration: 300 });
  }, [flowZoomIn]);

  const handleZoomOut = useCallback(() => {
    flowZoomOut({ duration: 300 });
  }, [flowZoomOut]);

  const handleFitView = useCallback(() => {
    fitView({ duration: 500, padding: 0.1 });
  }, [fitView]);

  return (
    <Panel position="bottom-right" className="m-6">
      <div className="flex flex-col gap-1 rounded-md border border-zinc-700 bg-zinc-800 p-1 shadow-lg">
        <button
          onClick={handleZoomIn}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Zoom In"
        >
          <FiZoomIn size={18} />
        </button>

        <button
          onClick={handleZoomOut}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Zoom Out"
        >
          <FiZoomOut size={18} />
        </button>

        <div className="my-1 h-px w-full bg-zinc-700"></div>

        <button
          onClick={handleFitView}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Fit View"
        >
          <TbArrowsMaximize size={18} />
        </button>
      </div>
    </Panel>
  );
};

const WorkflowHeader = ({
  workflowTitle,
  onTitleEdit,
  onAddAppIntegration,
  onClose,
  onShowHistory, // new prop received here
  onOpenFiles,
  handleExecuteWorkflow,
  onShowGlobalState,
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  return (
    <header className="flex items-center justify-between border-b border-zinc-700 bg-zinc-900 px-4 py-2 text-zinc-100">
      {/* Left section with title and edit */}
      <div className="flex items-center space-x-4 max-sm:w-full max-sm:justify-between">
        <h1 className="max-w-32 truncate text-ellipsis text-xl font-semibold">
          {workflowTitle || 'Untitled Workflow'}
        </h1>
        <div className="relative flex max-sm:space-x-2">
          <button
            onClick={onTitleEdit}
            Add
            commentMore
            actions
            className="rounded-full p-1 transition-colors hover:bg-zinc-700"
          >
            <AiOutlineEye className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowMobileMenu((prev) => !prev)}
            className="hidden rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100 max-sm:block"
          >
            <FiChevronDown
              className={`h-5 w-5 transition-transform duration-200 ${
                showMobileMenu ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100 sm:hidden"
            title="Close"
          >
            <FiX className="h-5 w-5" />
          </button>

          {/* Mobile Menu Dialog */}
          {showMobileMenu && (
            <div className="absolute right-0 top-10 z-50 mt-2 space-y-2 rounded-md bg-zinc-800 p-4 shadow-md sm:hidden">
              <button
                onClick={onAddAppIntegration}
                className="flex w-full items-center rounded-md bg-zinc-700 px-3 py-2 transition-colors hover:bg-zinc-600"
              >
                <FaToolbox className="mr-2 h-4 w-4" />
                <span>Integrations</span>
              </button>
              <button
                onClick={onOpenFiles}
                className="flex w-full items-center rounded-md bg-zinc-700 px-3 py-2 hover:bg-zinc-600"
              >
                <FiFolder className="mr-2 h-4 w-4" />
                <span>Files</span>
              </button>
              <button
                onClick={handleExecuteWorkflow}
                className="w-full rounded bg-zinc-700 px-4 py-2 text-white hover:bg-zinc-600"
              >
                Execute Workflow
              </button>
              <button
                onClick={onShowHistory}
                className="w-full rounded bg-zinc-700 px-4 py-2 text-white hover:bg-zinc-600"
              >
                Execution History
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Center section with add node buttons */}
      <div className="hidden items-center space-x-3 sm:flex">
        <button
          onClick={onAddAppIntegration}
          className="flex items-center rounded-md bg-zinc-800 px-3 py-1.5 transition-colors hover:bg-zinc-700"
        >
          <FaToolbox className="mr-2 h-4 w-4" />
          <span>Integrations</span>
        </button>
        <button
          onClick={onOpenFiles}
          className="flex items-center rounded-md bg-zinc-800 px-3 py-1.5 hover:bg-zinc-700"
        >
          <FiFolder className="mr-2 h-4 w-4" />
          <span>Files</span>
        </button>
        {/* --- NEW: Global State Button --- */}
        <button
          onClick={onShowGlobalState}
          className="flex items-center rounded-md bg-zinc-800 px-3 py-1.5 transition-colors hover:bg-zinc-700"
        >
          <Globe className="mr-2 h-4 w-4" />
          <span>Global State</span>
        </button>
      </div>

      {/* Right Section: Workflow Controls */}
      <div className="hidden items-center space-x-3 sm:flex">
        <div className="mr-4 flex items-center space-x-2">
          <button
            className="rounded bg-zinc-800 px-4 py-2 text-white hover:bg-zinc-700"
            onClick={handleExecuteWorkflow}
          >
            Execute Workflow
          </button>
          {/* New Execution History Button */}
          <button
            className="rounded bg-zinc-800 px-4 py-2 text-white hover:bg-zinc-700"
            onClick={onShowHistory}
          >
            Execution History
          </button>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
            title="Close"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};
